import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { create_template_request } from '@/api/template-requests';
import { toast } from '@/hooks/use-toast';
import { Loader2, Upload, X } from 'lucide-react';

interface CreateTemplateRequestDialogProps {
    trigger?: React.ReactNode;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    onSuccess?: () => void;
}

export function CreateTemplateRequestDialog({
    trigger,
    open,
    onOpenChange,
    onSuccess
}: CreateTemplateRequestDialogProps) {
    const { t, i18n } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [internalOpen, setInternalOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const isControlled = open !== undefined;
    const showOpen = isControlled ? open : internalOpen;
    const setShowOpen = isControlled ? onOpenChange! : setInternalOpen;

    const { register, handleSubmit, reset, formState: { errors } } = useForm<{
        description: string;
    }>();

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        setSelectedFile(file);
    };

    const handleRemoveFile = () => {
        setSelectedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const onSubmit = async (data: { description: string }) => {
        if (!selectedFile) {
            toast({
                title: t('error'),
                description: t('please_select_file', 'Veuillez sélectionner un fichier'),
                variant: 'destructive',
            });
            return;
        }

        setLoading(true);
        try {
            await create_template_request({
                file: selectedFile,
                description: data.description,
            });

            toast({
                title: t('success'),
                description: t('request_submitted'),
            });

            reset();
            setSelectedFile(null);
            setShowOpen(false);
            onSuccess?.();
        } catch (error) {
            toast({
                title: t('error'),
                description: t('error_fetching_requests'),
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={showOpen} onOpenChange={setShowOpen}>
            {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
            <DialogContent className="sm:max-w-[500px]" dir={i18n.dir()}>
                <DialogHeader className="text-start">
                    <DialogTitle>{t('upload_template_request')}</DialogTitle>
                    <p className="text-xs text-muted-foreground">{t('upload_template_subtitle')}</p>
                    <DialogDescription className="text-start">
                        {t('upload_template_desc')}
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-4">
                    <div className="space-y-2">
                        <Label>{t('file')}</Label>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".docx,.doc"
                            onChange={handleFileChange}
                            className="hidden"
                        />
                        <div className="flex items-center gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => fileInputRef.current?.click()}
                                className="flex items-center gap-2"
                            >
                                <Upload className="h-4 w-4" />
                                {t('choose_file')}
                            </Button>
                            {selectedFile ? (
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <span className="truncate max-w-[200px]">{selectedFile.name}</span>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleRemoveFile}
                                        className="h-6 w-6 p-0"
                                    >
                                        <X className="h-4 w-4" />
                                    </Button>
                                </div>
                            ) : (
                                <span className="text-sm text-muted-foreground">
                                    {t('no_file_selected')}
                                </span>
                            )}
                        </div>
                        <p className="text-xs text-muted-foreground">{t('file_format_note')}</p>
                        {!selectedFile && errors.description && (
                            <p className="text-sm text-destructive">{t('field_required')}</p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="description">{t('description_label')}</Label>
                        <Textarea
                            id="description"
                            placeholder={t('description_placeholder')}
                            {...register('description')}
                        />
                    </div>

                    <p className="text-xs text-muted-foreground italic">{t('reassuring_message')}</p>

                    <div className="flex justify-end pt-4">
                        <Button type="submit" disabled={loading}>
                            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {t('upload_template_request')}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
